import { FC, useMemo, useState } from 'react'
import { createSearchParams, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import { FieldArray } from 'react-final-form-arrays'
import { PageLoader } from 'components/PageLoader'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { FIND_ONE_DENTAL_SCHOOL_LOCATION, UPDATE_DENTAL_SCHOOL_LOCATION } from 'queries/dentalSchools'
import {
  findOneDentalSchoolLocation,
  findOneDentalSchoolLocationVariables,
} from '__generatedTypes__/findOneDentalSchoolLocation'
import {
  updateDentalSchoolLocation,
  updateDentalSchoolLocationVariables,
} from '__generatedTypes__/updateDentalSchoolLocation'
import { QUERY_PARAM, SCHOOL_STATISTICS_TAB } from 'pagesNew/AdmissionManagement'
import { findAllDentalSchools_findAllDentalSchool_items as DentalSchool } from '__generatedTypes__/findAllDentalSchools'
import { validateRequiredField } from 'utils/validation'
import { toast } from 'utils/toast'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { BROADCAST_CHANNEL } from 'constants/consts'
import { REFETCH_DENTAL_SCHOOLS_MESSAGE } from 'pagesNew/DentalSchools/Create'
import {
  DentalSchoolsFieldArray,
  Props as DentalSchoolsFieldArrayProps,
} from '../OrganizeCreateDentalSchoolStatistic/Form/DentalSchoolsFieldArray'
import { Form, FormValues, getSchoolLocations } from '../OrganizeCreateDentalSchoolStatistic/Form'

type Props = {
  platform: Platform
  isOptometry?: boolean
}

export const OrganizeEditDentalSchoolStatistic: FC<Omit<Props, 'platform'>> = withPlatformCheck(
  ({ platform, isOptometry }: Props) => {
    const { id: sectionId } = useParams()
    const [isPlatformMatch, setIsPlatformMatch] = useState(true)
    const [isTypeMatch, setIsTypeMatch] = useState(true)
    const { navigate } = useLink()

    const { data, loading } = useQuery<findOneDentalSchoolLocation, findOneDentalSchoolLocationVariables>(
      FIND_ONE_DENTAL_SCHOOL_LOCATION,
      {
        variables: {
          id: sectionId || '',
        },
        skip: !sectionId,
        onCompleted: (data) => {
          const dentalSchoolSection = data?.findOneDentalSchoolLocation

          setIsPlatformMatch(dentalSchoolSection?.platform.id === platform.id)
          setIsTypeMatch(dentalSchoolSection?.isOptometry === !!isOptometry)
        },
      }
    )

    const [updateDentalSchoolLocation] = useMutation<updateDentalSchoolLocation, updateDentalSchoolLocationVariables>(
      UPDATE_DENTAL_SCHOOL_LOCATION
    )

    const handleSubmit = async (values: FormValues) => {
      try {
        const { id, title, description = '' } = values
        await updateDentalSchoolLocation({
          variables: {
            updateDentalSchoolLocationInput: {
              id,
              title,
              description,
              schoolLocationType: values.schoolLocation.id,
              platform: {
                id: platform.id,
              },
              dentalSchool: values.schools.map(({ id }) => ({ id })),
              dentalSchoolIds: values.schools.map(({ id }) => id),
            },
          },
        })

        navigate(
          isOptometry ? cmsRoutes.OPTOMETRY : cmsRoutes.DENTISTRY,
          {},
          { searchParams: createSearchParams({ [QUERY_PARAM]: SCHOOL_STATISTICS_TAB }) }
        )
        toast.success(`${isOptometry ? 'Optometry' : 'Dental'} school statistics section updated successfully`)
      } catch (error) {
        toast.error(`Failed to update ${isOptometry ? 'optometry' : 'dental'} school statistics section`)
      }
    }

    useBroadcastChannel(BROADCAST_CHANNEL, (event) => {
      if (event.data === REFETCH_DENTAL_SCHOOLS_MESSAGE) {
        toast.success(`${isOptometry ? 'Optometry' : 'Dental'} school statistics created successfully`)
      }
    })

    const initialValues: FormValues = useMemo(() => {
      const dentalSchools = data?.findOneDentalSchoolLocation?.dentalSchool || []

      const schoolsOrder: FormValues['schools'] =
        (data?.findOneDentalSchoolLocation?.dentalSchoolIds
          .map((id) => dentalSchools.find(({ id: dentalSchoolId }) => id === dentalSchoolId))
          .filter((dentalSchool) => !!dentalSchool) as unknown as FormValues['schools']) || []

      const schoolLocations = getSchoolLocations(isOptometry)

      return {
        id: sectionId,
        title: data?.findOneDentalSchoolLocation?.title || '',
        description: data?.findOneDentalSchoolLocation?.description || '',
        schoolLocation:
          schoolLocations.find(({ id }) => id === data?.findOneDentalSchoolLocation?.schoolLocationType) ??
          schoolLocations[0],
        schools: schoolsOrder,
      }
    }, [loading, isOptometry])

    if (loading) {
      return <PageLoader />
    }

    if (!isPlatformMatch) {
      return (
        <Typography>
          You are trying to edit dental school statistics with different platform (platform in url: {platform.title},
          dental school statistics platform: {data?.findOneDentalSchoolLocation?.platform?.title})
        </Typography>
      )
    }

    if (!isTypeMatch) {
      return (
        <Typography>
          You are trying to edit school statistics with different type (type in url:{' '}
          {isOptometry ? 'Optometry' : 'Dental'}, school statistics type:{' '}
          {data?.findOneDentalSchoolLocation?.isOptometry ? 'Optometry' : 'Dental'},)
        </Typography>
      )
    }

    return (
      <Form
        platform={platform}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        title={`Organize ${isOptometry ? 'Optometry' : 'Dental'} School Statistics`}
        createLink={isOptometry ? cmsRoutes.CREATE_OPTOMETRY_SCHOOL : cmsRoutes.CREATE_DENTAL_SCHOOL}
        renderFields={({ form, linkParams }) => (
          <FieldArray<DentalSchool> name="schools" validate={validateRequiredField}>
            {({ fields, meta }) => (
              <DentalSchoolsFieldArray
                platform={platform}
                isOptometry={!!isOptometry}
                onChange={form.change as unknown as DentalSchoolsFieldArrayProps['onChange']}
                fields={fields}
                meta={meta}
                linkParams={linkParams}
              />
            )}
          </FieldArray>
        )}
        isOptometry={isOptometry}
      />
    )
  }
)
